/**
 * Round 20 U7 -- what is new since this person last looked.
 *
 * On an estate already in alarm, home read DEGRADED with 9 of 9
 * impacted, and one more critical finding moved none of those numbers.
 * This lists the critical and high findings raised since the reader's
 * last look at this surface, newest first, then records the look.
 *
 * The list fetched on arrival is kept for the visit, so the reader is
 * not shown news and then has it vanish under them; the next visit
 * starts from this one. With nothing new (or a first visit, which has
 * no baseline) it renders nothing.
 */
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { api } from "shared/api/client";
import { formatDateTime } from "shared/time/formatDate";

export type WhatsNewSurface = "home" | "cascade";

export interface WhatsNewItem {
  finding_id: string;
  title: string;
  severity: string;
  raised_at: string;
  entity_kind: string | null;
  entity_id: string | null;
  is_breach: boolean;
}

export interface WhatsNew {
  surface: WhatsNewSurface;
  last_looked_at: string | null;
  total: number;
  items: WhatsNewItem[];
}

export function SinceLastLooked({ surface }: { surface: WhatsNewSurface }) {
  const { t, i18n } = useTranslation();
  const news = useQuery({
    queryKey: ["me", "whats-new", surface],
    queryFn: () =>
      api.get<WhatsNew>(`/v1/me/whats-new?surface=${encodeURIComponent(surface)}`),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const seen = useMutation({
    mutationFn: () =>
      api.post(`/v1/me/whats-new/seen?surface=${encodeURIComponent(surface)}`, {}),
  });
  const stamped = useRef(false);
  const { mutate } = seen;
  useEffect(() => {
    if (news.isSuccess && !stamped.current) {
      stamped.current = true;
      mutate();
    }
  }, [news.isSuccess, mutate]);

  const data = news.data;
  if (!data || !data.last_looked_at || data.items.length === 0) return null;
  const more = data.total - data.items.length;
  return (
    <section
      data-testid={`since-last-looked-${surface}`}
      className="rounded-lg border border-rag-red/30 bg-white p-4"
    >
      <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-rag-red">
        {t("modules.twin.whatsNew.heading", {
          since: formatDateTime(data.last_looked_at, i18n.language),
        })}
      </p>
      <p className="mt-1 text-sm text-slate-700">
        {t("modules.twin.whatsNew.summary", { count: data.total })}
      </p>
      <ol className="mt-2 flex flex-col gap-1">
        {data.items.map((item) => (
          <li
            key={item.finding_id}
            data-testid={`since-last-looked-item-${item.finding_id}`}
            className="flex flex-wrap items-baseline gap-x-2 text-sm"
          >
            <span className="tabular-nums text-slate-500">
              {formatDateTime(item.raised_at, i18n.language)}
            </span>
            <span className="font-semibold text-rag-red">
              {t(`board.severity.${item.severity}`)}
            </span>
            {item.is_breach && (
              <span className="rounded bg-rag-redBg px-1.5 text-[11px] text-rag-red">
                {t("modules.twin.whatsNew.breach")}
              </span>
            )}
            <Link
              to={`/twin/findings/${item.finding_id}`}
              className="text-slate-800 hover:underline"
            >
              {item.title}
            </Link>
          </li>
        ))}
      </ol>
      {more > 0 && (
        <p className="mt-1 text-xs text-slate-500">
          {t("modules.twin.whatsNew.more", { count: more })}
        </p>
      )}
    </section>
  );
}
